/**
 * Colour a project carries as its billing milestones get ticked off, so a list
 * of projects can be read at a glance.
 *
 * One tick, one colour: the step is the *count* of milestones reached, not the
 * fraction of them. Scaling by fraction seems tidier — union projects have a
 * "supplies paid" milestone that non-union ones don't, so their totals differ —
 * but it means the same state shows up two different colours. A project with
 * only "Sent to Client" ticked came out slate at 1-of-5 and sky at 1-of-4,
 * which is indefensible when you are looking at the two side by side.
 *
 * Being done always wins the last colour, whether that took four ticks or five.
 * Sorting still uses the fraction — see `projectProgressRatio`.
 */

/** The API's zero value for a date: this milestone hasn't happened yet. */
export const UNSET_DATE = "0001-01-01T00:00:00Z";

/**
 * The fields milestones are derived from. Structural rather than the Project
 * entity so this module stays importable on its own — the entity reaches into
 * the app's constants, which drag in MUI and don't load under vitest.
 */
export type ProjectMilestoneFields = {
  clientInvoiceDate: string;
  clientPaymentDate: string;
  employeesInvoiceDate: string;
  employeesPaymentDate: string;
  suppliesPaymentDate: string;
  union: boolean;
};

/**
 * Billing milestones in the order Project Info ticks them off. "Supplies paid"
 * only exists on union projects, so a project has 4 or 5 of them.
 *
 * @param {object} project Project to read the milestone dates off.
 * @returns {Array} One entry per milestone, true once it has been reached.
 */
export function projectMilestones(project: ProjectMilestoneFields): boolean[] {
  const reached = (date: string) => date !== UNSET_DATE;
  const milestones = [
    reached(project.clientInvoiceDate),
    reached(project.clientPaymentDate),
    reached(project.employeesInvoiceDate),
    reached(project.employeesPaymentDate),
  ];
  if (project.union) milestones.push(reached(project.suppliesPaymentDate));
  return milestones;
}

/**
 * Date field behind each milestone, in the same order as `projectMilestones`.
 * Non-union projects stop after the fourth.
 */
export const PROJECT_MILESTONE_FIELDS = [
  "clientInvoiceDate",
  "clientPaymentDate",
  "employeesInvoiceDate",
  "employeesPaymentDate",
  "suppliesPaymentDate",
] as const;

/**
 * Labels for each milestone, in the same order as `projectMilestones`. Union
 * projects deal with employees where non-union ones deal with vendors.
 *
 * @param {boolean} union Whether the project is a union project.
 * @returns {Array} One label per milestone.
 */
export function projectMilestoneLabels(union: boolean): string[] {
  const labels = [
    "Sent to Client",
    "Paid by Client",
    union ? "Sent to employees" : "Sent to vendors",
    union ? "Paid to employees" : "Paid to vendors",
  ];
  if (union) labels.push("Supplies paid");
  return labels;
}

/**
 * @param {object} project Project to count.
 * @returns {number} How many milestones have been ticked off.
 */
export function projectMilestonesReached(
  project: ProjectMilestoneFields
): number {
  return projectMilestones(project).filter((reached) => reached).length;
}

/**
 * @param {object} project Project to count.
 * @returns {number} How many milestones this project has (4 or 5).
 */
export function projectMilestonesTotal(
  project: ProjectMilestoneFields
): number {
  return projectMilestones(project).length;
}

/**
 * Every milestone ticked off: nothing is left to invoice or pay, so the
 * project is ready to move to the Completed tab.
 *
 * @param {object} project Project to check.
 * @returns {boolean} True once all milestones are reached.
 */
export function projectCompleted(project: ProjectMilestoneFields): boolean {
  return projectMilestonesReached(project) === projectMilestonesTotal(project);
}

/**
 * One colour per step rather than one hue at five opacities. Shades of a
 * single colour are hard to tell apart without a neighbouring row to compare
 * against, which is exactly the comparison you can't make once the grid is
 * sorted or filtered.
 *
 * `tint` washes the row and has to stay light enough to read black text over.
 * `accent` is the saturated version, filling the stepper's segments and the
 * ticked-off chips. `ink` is what stays readable on top of `accent` — white on
 * everything except amber, which is far too light to carry it.
 *
 * Ordered so the last step is the green everyone already reads as "done", and
 * the one before it is amber — nearly there.
 *
 * To restyle the scale, edit this table and nothing else.
 */
const PROGRESS_PALETTE: Array<{ tint: string; accent: string; ink: string }> = [
  { tint: "rgba(84, 110, 122, 0.15)", accent: "#546E7A", ink: "#FFFFFF" }, // slate
  { tint: "rgba(2, 119, 189, 0.15)", accent: "#0277BD", ink: "#FFFFFF" }, // sky
  { tint: "rgba(126, 87, 194, 0.15)", accent: "#7E57C2", ink: "#FFFFFF" }, // violet
  { tint: "rgba(249, 168, 37, 0.2)", accent: "#F9A825", ink: "#3E2C00" }, // amber
  { tint: "rgba(46, 125, 50, 0.22)", accent: "#2E7D32", ink: "#FFFFFF" }, // green — complete
];

/** Number of steps in the scale. */
export const PROJECT_PROGRESS_STEPS = PROGRESS_PALETTE.length;

/**
 * How far along a project is, as a fraction.
 *
 * This — not the raw count — is what the Progress column sorts on. Counting
 * milestones puts a finished non-union project (4 of 4) level with a union one
 * that still owes supplies (4 of 5), and a 3-of-4 level with a 3-of-5, so the
 * sorted column looks scrambled next to the bars it's meant to order.
 *
 * @param {number} reached Milestones ticked off.
 * @param {number} total Milestones this project has (4 or 5).
 * @returns {number} 0 through 1.
 */
export function projectProgressRatio(reached: number, total: number): number {
  if (reached <= 0 || total <= 0) return 0;
  return Math.min(1, reached / total);
}

/**
 * Position a project occupies on the colour scale.
 *
 * One step per milestone reached, so the same number of ticks is always the
 * same colour. Finishing jumps to the last step regardless of how many
 * milestones that took, which is why a non-union project goes straight from
 * violet to green and never passes through amber: amber means "four done,
 * still not finished", and only a union project can be in that state.
 *
 * @param {number} reached Milestones ticked off.
 * @param {number} total Milestones this project has (4 or 5).
 * @returns {number} 0 when nothing is ticked, otherwise 1..PROJECT_PROGRESS_STEPS.
 */
export function projectProgressStep(reached: number, total: number): number {
  if (reached <= 0 || total <= 0) return 0;
  if (reached >= total) return PROJECT_PROGRESS_STEPS;
  return Math.min(PROJECT_PROGRESS_STEPS - 1, reached);
}

/**
 * Row class carrying a step's shade. Empty for step 0 so untouched rows keep
 * the grid's own background (including its hover and selection states).
 *
 * @param {number} step Step from `projectProgressStep`.
 * @returns {string} Class name, or "" when the project has no milestones yet.
 */
export function projectProgressClass(step: number): string {
  return step > 0 ? `projectProgress${step}` : "";
}

/**
 * Row wash for a step.
 *
 * @param {number} step Step from `projectProgressStep`.
 * @returns {string} CSS colour, "transparent" when the project has no
 * milestones yet.
 */
export function projectProgressColor(step: number): string {
  return PROGRESS_PALETTE[step - 1]?.tint ?? "transparent";
}

/**
 * Saturated colour for a step, for the stepper's filled segments.
 *
 * @param {number} step Step from `projectProgressStep`.
 * @returns {string} CSS colour. Grey when the project has no milestones yet.
 */
export function projectProgressAccent(step: number): string {
  return PROGRESS_PALETTE[step - 1]?.accent ?? "#BDBDBD";
}

/**
 * Text and icon colour that stays readable on top of a step's accent.
 *
 * @param {number} step Step from `projectProgressStep`.
 * @returns {string} CSS colour.
 */
export function projectProgressInk(step: number): string {
  return PROGRESS_PALETTE[step - 1]?.ink ?? "rgba(0, 0, 0, 0.87)";
}

/**
 * `sx` entries painting every step onto a DataGrid's rows.
 *
 * Written as `sx` rather than a stylesheet on purpose: the emitted selector
 * outranks the grid's own `.MuiDataGrid-row:hover` rule, which a plain
 * stylesheet of equal specificity would lose to depending on injection order.
 *
 * @returns {object} `sx` fragment to spread onto the DataGrid.
 */
export function projectProgressRowStyles(): Record<
  string,
  { backgroundColor: string; backgroundImage?: string }
> {
  const styles: Record<
    string,
    { backgroundColor: string; backgroundImage?: string }
  > = {};
  const shade = "rgba(0, 0, 0, 0.07)";
  for (let step = 1; step <= PROJECT_PROGRESS_STEPS; step += 1) {
    const row = `& .MuiDataGrid-row.${projectProgressClass(step)}`;
    styles[row] = { backgroundColor: projectProgressColor(step) };
    // Darken on hover rather than deepening the tint: the tints are already
    // as strong as they can be with black text over them, and this keeps the
    // step's hue visible under the cursor.
    styles[`${row}:hover`] = {
      backgroundColor: projectProgressColor(step),
      backgroundImage: `linear-gradient(${shade}, ${shade})`,
    };
  }
  return styles;
}
